import { useState } from 'react'
import {
  useResetRequests,
  useResolveResetRequest,
  useIgnoreResetRequest,
} from '@/hooks/backoffice/useResetRequests'
import { Button } from '@/components/ui/button'
import { CopyableLink } from '@/components/CopyableLink'
import type { ResetRequestStatus, ResolveResetRequestResult } from '@/types/backoffice'

const STATUS_LABELS: Record<ResetRequestStatus, string> = {
  PENDING: 'Pendentes',
  RESOLVED: 'Resolvidos',
  IGNORED: 'Ignorados',
}

function formatRelative(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime()
  const min = Math.floor(ms / 60000)
  if (min < 1) return 'agora mesmo'
  if (min < 60) return `${min} min atrás`
  const h = Math.floor(min / 60)
  if (h < 24) return `${h}h atrás`
  return `${Math.floor(h / 24)}d atrás`
}

export function ResetRequestsPage() {
  const [status, setStatus] = useState<ResetRequestStatus>('PENDING')
  const { data, isLoading } = useResetRequests(status)
  const resolve = useResolveResetRequest()
  const ignore = useIgnoreResetRequest()
  const [resolved, setResolved] = useState<{ id: string; result: ResolveResetRequestResult } | null>(null)

  async function handleResolve(id: string) {
    const result = await resolve.mutateAsync(id)
    setResolved({ id, result })
  }

  async function handleIgnore(id: string) {
    if (!confirm('Marcar este pedido como ignorado?')) return
    await ignore.mutateAsync(id)
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-foreground">Pedidos de reset de senha</h1>
        <p className="text-sm text-muted-foreground">
          Solicitações enviadas via tela pública “Esqueci minha senha”.
        </p>
      </div>

      <div className="flex overflow-hidden rounded-md border border-input text-sm">
        {(Object.keys(STATUS_LABELS) as ResetRequestStatus[]).map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setStatus(s)}
            className={`px-3 py-1.5 transition-colors ${
              status === s ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted/50'
            }`}
          >
            {STATUS_LABELS[s]}
          </button>
        ))}
      </div>

      {isLoading && <p className="text-sm text-muted-foreground">Carregando…</p>}
      {!isLoading && data && data.length === 0 && (
        <p className="rounded-md border border-border bg-muted/30 px-3 py-6 text-center text-sm text-muted-foreground">
          Nenhum pedido nesta visão.
        </p>
      )}

      <ul className="space-y-2">
        {data?.map((req) => (
          <li key={req.id} className="rounded-lg border border-border bg-card p-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="font-medium text-foreground">{req.email}</p>
                <p className="text-xs text-muted-foreground">
                  {formatRelative(req.createdAt)}
                  {req.resolver && ` · resolvido por ${req.resolver.name}`}
                </p>
              </div>
              {req.status === 'PENDING' && (
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => handleIgnore(req.id)}>
                    Ignorar
                  </Button>
                  <Button size="sm" onClick={() => handleResolve(req.id)} disabled={resolve.isPending}>
                    Resolver
                  </Button>
                </div>
              )}
            </div>
            {resolved?.id === req.id && (
              <div className="mt-3 space-y-2 border-t border-border pt-3">
                {resolved.result.resetUrl ? (
                  <CopyableLink
                    url={resolved.result.resetUrl}
                    hint={`Envie este link a ${req.email}. Expira em ${new Date(resolved.result.expiresAt).toLocaleString('pt-BR')}.`}
                  />
                ) : (
                  <p className="text-xs text-muted-foreground">
                    Nenhum usuário ativo encontrado para este email — pedido marcado como resolvido sem token gerado.
                  </p>
                )}
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  )
}
